import './MicroAppSelection.css'

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { MaterialSymbol } from 'react-material-symbols'
import { useNavigate } from 'react-router-dom'

import { Button } from 'primereact/button'
import { ListBox, ListBoxChangeEvent } from 'primereact/listbox'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Skeleton } from 'primereact/skeleton'

import { Project } from '../../../../../data/models'
import { useAllProjectsByTenantId } from '../../../../../data/tenants'
import { useProjectsByReadPermission } from '../../../../../features/authorization/data/hooks'
import {
  setCurrentTenantConfig,
  useCurrentTenantConfig,
} from '../../../../../utils/tenants'

export const MicroAppSelection: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tenantId, projectId } = useCurrentTenantConfig()
  const { data, isLoading } = useAllProjectsByTenantId(tenantId as string)
  const projectsWithReadPermission = useProjectsByReadPermission()

  const panelRef = useRef<OverlayPanel>(null)

  // If there is only one project available, set it as the current project
  useEffect(() => {
    if (!isLoading && data && !projectId) {
      const availableProjects = data.filter(p =>
        projectsWithReadPermission.includes(p.Id),
      )
      if (availableProjects.length === 1) {
        const singleProjectId = availableProjects[0].Id
        setCurrentTenantConfig({ tenantId, projectId: singleProjectId })
      }
    }
  }, [data, isLoading, projectId, projectsWithReadPermission, tenantId])

  const handleDeselect = () => {
    setCurrentTenantConfig({ tenantId, projectId: undefined })
    panelRef.current?.hide()
    navigate('/')
  }

  const handleChange = (e: ListBoxChangeEvent) => {
    const newProjectId = e.value

    if (!newProjectId || newProjectId === projectId) {
      handleDeselect()
      panelRef.current?.hide()
      return
    }

    setCurrentTenantConfig({ tenantId, projectId: newProjectId })
    panelRef.current?.hide()
    navigate('/')
  }

  const itemTemplate = (option: Project) => {
    return (
      <div className="flex align-items-center">
        <div className="flex flex-column my-1 mr-3">
          <div className="font-semibold">{option.Name}</div>
          <div className="text-sm font-italic">{option.Id}</div>
        </div>
        {option.Id === projectId && (
          <div className="ml-auto">
            <i className="pi pi-check"></i>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) return <Skeleton />

  if (projectsWithReadPermission.length < 1) return null

  return (
    <>
      <Button
        link
        aria-label="Micro app selection"
        onClick={e => panelRef.current?.toggle(e, undefined)}
        icon={options => <MaterialSymbol icon="warehouse" size={24} />}
        className="header__micro-app-selection p-2 border-none"
      >
        <span className="hidden md:inline ml-2">
          {data?.filter(p => p.Id === projectId)[0]?.Name ||
            t('tenant.projectDropdown.placeholder.select')}
        </span>
      </Button>

      <OverlayPanel ref={panelRef} pt={{ content: { className: 'p-0' } }}>
        <ListBox
          value={projectId}
          filter
          filterBy="Name,Id"
          itemTemplate={itemTemplate}
          onChange={handleChange}
          options={data
            ?.filter(p => projectsWithReadPermission.includes(p.Id))
            .sort((a: Project, b: Project) => {
              if (a.Name < b.Name) return -1
              return 1
            })}
          optionLabel="Name"
          optionValue="Id"
          className="w-full md:w-14rem border-none"
          listStyle={{ maxHeight: '250px' }}
        />
      </OverlayPanel>
    </>
  )
}
